import { useWidget, VoteAPIResponseCode } from '@telescope/cassini-hooks';
import { Box, Button, FormControl, VStack, Divider, useColorModeValue, Flex } from '@telescope/cassini-ui';
import { Heading, Text } from 'components';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { isEmpty } from 'lodash';
import TagManager from 'react-gtm-module';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import reactStringReplace from 'react-string-replace';
import { ErrorType } from 'types';
import { ROUTES } from 'utils';
import { useAuth } from '../lib';
import { AuthMethod } from '../utils/constants';
import { InputName, renderInput } from 'components'
import { FacebookButton } from './FacebookButton';
import { ErrorWarning } from './ErrorWarning';
import { APP_MAX_WIDTH } from 'utils';
import { Open } from 'types';
import { AvailablePrizeMessage } from './AvailablePrizeMessage';

export const Login = () => {
  const { loginWithEmail, loginWithFacebook, isLoggingIn } = useAuth();
  const { data: authData } = useWidget({ select: (data: Open) =>  data.widget.global_views.auth });
  const { content: { loginModal: content, unavailablePrizesMessage }, settings: { isAgeGateEnabled, minAge, isUnavailablePrizesMessageEnabled } } = authData!;
  
  const { data: errorData } = useWidget({ select: (data: Open) => data.widget.global_views.errors });
  const errorCopy = errorData!;
  const formMethods = useForm<{ [x: string]: any }>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  });
  const { handleSubmit, setValue, watch, register, getValues, trigger, setError, formState: { errors } } = formMethods;
  const [formError, setFormError] = useState<React.ReactNode>(null);
  const hasError = !isEmpty(errors);

  const { isMethodEnabled, authMethods, hasRegistration } = useAuth();
  const hasMultipleMethods = Object.values(authMethods).filter((method) => method).length > 1;
  const watchMethod = watch('method');
  const navigate = useNavigate();

  const dividerColor = useColorModeValue('gray.800', 'gray.400');

  // If hasRegistration is true, handle age gate during registration
  const hasAgeGate = useMemo(() => isAgeGateEnabled && !hasRegistration, [isAgeGateEnabled, hasRegistration]);

  useEffect(() => {
    register('method');
  }, [register]);

  const invalidEmailError = reactStringReplace(content.errorMessage.message, '{{LINK}}', (match, i) => (
    <Button
      fontSize="inherit"
      variant="link"
      colorScheme="inherit"
      onClick={() => navigate(ROUTES.REGISTER, { replace: true, state: { backgroundLocation: ROUTES.HOME } })}
      key={i}
      minW={0}>
      {content.errorMessage.linkText}
    </Button>
  ));

  const isAgeAllowed = useCallback(() => {
    if (!hasAgeGate) return true;

    const dob = new Date(getValues(content.dobInput.fieldType));
    const now = new Date();
    let yearsDiff = now.getFullYear() - dob.getFullYear();

    return yearsDiff >= parseInt(minAge, 10);
  }, [hasAgeGate]);

  const handleFacebookLogin = async () => {
    setFormError(null);
    setValue('method', AuthMethod.FACEBOOK);

    handleSubmit(async (formData: any) => {
      if (!isAgeAllowed()) {
        const errorMessage = content.dobInput.notAllowedErrorMessage.replace('{{MIN_AGE}}', minAge);
        return setError(InputName.DOB, { type: 'notAllowed', message: errorMessage });
      }

      try {
        await loginWithFacebook(formData);
        TagManager.dataLayer({ dataLayer: { event: 'login', method: AuthMethod.FACEBOOK } });
        navigate(ROUTES.HOME, { replace: true });
      } catch (err: any) {
        if (err.message === VoteAPIResponseCode.GENERAL_INVALID && hasRegistration) {
          return setFormError(invalidEmailError);
        }
        if (err.message === ErrorType.FB_PERMISSIONS) {
          return setFormError(errorCopy.content.fbPermissions.copy.description);
        }
        setFormError(errorCopy.content.generic.copy.description);
      }
    })();
  };

  const handleOAuthLogin = () => {
    // TODO
  };

  const handleEmailLogin = async () => {
    setFormError(null);
    setValue('method', AuthMethod.EMAIL);

    // If no email is entered:
    // Trigger all fields to check for other errors
    // Set email error
    if (!getValues(InputName.EMAIL)) {
      await trigger();
      return setError(InputName.EMAIL, { type: 'required', message: content.emailInput.errorMessage });
    }

    handleSubmit(async (formData: any) => {
      if (!isAgeAllowed()) {
        const errorMessage = content.dobInput.notAllowedErrorMessage.replace('{{MIN_AGE}}', minAge);
        return setError(InputName.DOB, { type: 'notAllowed', message: errorMessage });
      }

      try {
        await loginWithEmail(formData);
        TagManager.dataLayer({ dataLayer: { event: 'login', method: AuthMethod.EMAIL } });
        navigate(ROUTES.HOME, { replace: true });
      } catch (err: any) {
        if (err === VoteAPIResponseCode.OVERLIMIT) {
          return navigate(`${ROUTES.ERROR}/${ErrorType.OVERLIMIT}`);
        }

        setFormError(hasRegistration? invalidEmailError : errorCopy.content.generic.copy.description);        
      }
    })();
  };

  return (
    <Box maxW={APP_MAX_WIDTH.TABLET} mx="auto">
      <Box mb={10} textAlign="center">
        <Heading as="h2" allowMarkdown mb={4}>
          {content.headline}
        </Heading>

        <Text allowMarkdown mb={4}>
          {content.description}
        </Text>

        <AvailablePrizeMessage />
      </Box>

      <FormProvider {...formMethods}>
        <FormControl isInvalid={!!formError || !!hasError}>
          <ErrorWarning>
            {formError || content.errorMessage.generic}
          </ErrorWarning>
        </FormControl>

        <form
          id="loginForm"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
          }}>
          
          <VStack>
            {content.inputs?.map((field: any, index: number) => {
              return <React.Fragment key={`inputs-${index}`}>{renderInput({ ...field })}</React.Fragment>;
            })}

            {isAgeGateEnabled && (
              <Box w="100%">
                {renderInput({ ...content.dobInput })}
              </Box>
            )}

            {isMethodEnabled(AuthMethod.EMAIL) && (
              <Box w="100%">
                {renderInput(content.emailInput)}
                <Button
                  isDisabled={isLoggingIn}
                  isLoading={watchMethod === AuthMethod.EMAIL && isLoggingIn}
                  width="full"
                  type="button"
                  mt={3}
                  onClick={handleEmailLogin}>
                  {content.buttons.email}
                </Button>
              </Box>
            )}

            { hasMultipleMethods && isMethodEnabled(AuthMethod.EMAIL) && 
              <Text fontSize="sm" whiteSpace="nowrap" color={dividerColor} textAlign="center">
                {content.dividerText}
              </Text> }

            {isMethodEnabled(AuthMethod.FACEBOOK) && (
              <FacebookButton
                isDisabled={isLoggingIn}
                isLoading={watchMethod === AuthMethod.FACEBOOK && isLoggingIn}
                onClick={handleFacebookLogin}>
                {content.buttons.facebook}
              </FacebookButton>
            )}

            { hasMultipleMethods && isMethodEnabled(AuthMethod.EMAIL) && 
              <Divider borderColor={dividerColor}/> }

          {content.optins?.[0]?.fieldType &&
            <Box my={4} w="100%" textAlign="left">
              {content.optins.map((el: any, index: number) => (
                <React.Fragment key={`optins-${index}`}>{renderInput(el)}</React.Fragment>
              ))}
            </Box>}

            {hasRegistration && (
              <Flex justifyContent="center">
                <Button
                  form="loginForm"
                  size="md"
                  type="button"
                  variant="link"
                  onClick={() =>
                    navigate(ROUTES.REGISTER, { replace: true, state: { backgroundLocation: ROUTES.HOME } })
                  }>
                  {content.linkToRegistration}
                </Button>
              </Flex>
            )}

          </VStack>
        </form>
      </FormProvider>
    </Box>
  );
};
